import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';
import {AttrsSchema, Component} from 'panel';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';

import {
  DATASET_PROPERTY_NAME,
  MappedPropertyValues,
  PropertyType,
  ResourceType,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {filterAttributes, getOwnAttr, parseNumber} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {peoplePropertyValuesClient} from 'common/report/queries/query-api/people-property-values-client';
import {NAVIGATION_KEY_CODES, propagateEvent, rafRetry} from 'common/util/dom';
import {
  ConstantPropertyValueInfo,
  ConstantPropertyValues,
  ConstantPropertyValueSet,
  ConstantPropertyValuesInfo,
  getIconForPropertyType,
  getLabelForPropertyType,
  propertiesUtils,
  renamePropertyValue,
} from 'common/report/util/properties';
import {getPropertyValueMappings, syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {truncateMiddle} from 'common/util/string';
import {SelectionState} from 'common/components/items-menu/constants';
import {FLOWS_CONVERSION, FLOWS_CONVERSION_VALUES} from 'common/query-builder/query-block/properties-menu/constants';
import {SpecialTimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DAYS_OF_THE_WEEK, HOURS_OF_DAY, MPProperties} from 'common/constants';
import {LabelTheme, Section, SectionItem} from 'common/components/items-menu/types';
import {Segmentation} from 'common/types/reports/query';
import {getPropertyType} from 'common/query-builder/query-entry-section/row-types/row-util';

import {DATE_RANGE_DISPLAY_CONTEXT_MAP, PropertyFilterFactory} from '../models/utils';
import {isEnhancedListFilter} from '../utils';
import {
  DisplayContext as PropertyFilterDisplayContext,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from '../models/types';
import type {PropertyFilter} from '../models/property-filter';
import {template} from './template';
import css from './index.inline.styl';

interface PropertyValuesScreenState {
  loadingPropertyValues: boolean;
  datasets?: any;
  editingPropertyType: boolean;
  propertyFilter?: PropertyFilter;
  propertyValues?: string[];
  searchFilter: string;
  currentValues?: any[];
}

interface PropertyValuesScreenParams {
  listProperty?: Segmentation;
}

const MAX_SPECIFY_VALUE_LENGTH = 255;

export class PropertyValuesScreen extends Component<
  PropertyValuesScreenState,
  any,
  unknown,
  unknown,
  unknown,
  PropertyValuesScreenParams
> {
  private keydownProxyEl: HTMLElement;
  private handleKeyPress = null;

  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'display-context': {type: `string`, default: PropertyFilterDisplayContext.Default},
      'enable-enhanced-list-filters': `boolean`,
      'event-name': {
        type: `string`,
        default: null,
        description: `The name of the event, if this is an event property, It will be used to fetch the list of property values.`,
      },
      'event-names': {
        type: `json`,
        default: null,
        description: `Use instead of "event-name" if there are multiple events.`,
      },
      'disable-multi-select': `boolean`,
      'disable-specify': `boolean`,
      'display-value-only': `boolean`,
      'has-back-button': `boolean`,
      'project-id': `number`,
      'property-filter': `json`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      defaultState: {
        datasets: null,
        editingPropertyType: false,
        loadingPropertyValues: false,
        propertyFilter: null,
        propertyValues: null,
        searchFilter: null,
      },
      params: {
        listProperty: Object,
      },
      helpers: {
        ...super.config.helpers,
        parseNumber,
        filterAttributes,
        truncateMiddle,

        PropertyType,
        NumberOperator,
        StringOperator,
        PropertyFilterDisplayContext,

        // region Attribute accessors

        hasBackButton: () => {
          return this.attr(`has-back-button`);
        },

        getDisplayContext: () => {
          return this.attr(`display-context`);
        },

        canTypeCast: () => !this.isExperimentsPropertyFilter && !this.enhancedListFilterEnabled(),

        enhancedListFilterEnabled: () => this.enhancedListFilterEnabled(),

        // endregion

        // region Template helpers

        getKeydownProxyEl: (): HTMLElement => {
          const inputElement = this.el.querySelector<HTMLElement>(`input, mp-input`);
          return inputElement || this.keydownProxyEl;
        },

        getPropertyTypeIcon: () => {
          return getIconForPropertyType(getPropertyType(this.state.propertyFilter));
        },

        getPropertyLabel: () => {
          const {propertyName, resourceType, dataGroupId, customProperty} = this.state.propertyFilter;
          return propertiesUtils.renameProperty(propertyName, resourceType, {dataGroupId, customProperty});
        },

        getPropertyTypeMenuSections: () => {
          const typeCastTypes = this.state.propertyFilter.getPropertyTypes();
          const {propertyType, propertyDefaultType} = this.state.propertyFilter;
          const types = [
            ...typeCastTypes,
            ...(typeCastTypes.includes(propertyDefaultType) ? [] : [propertyDefaultType]),
          ];

          const items = types.map((typeCastType) => {
            return {
              icon: getIconForPropertyType(typeCastType),
              isSelected: typeCastType === propertyType,
              label: getLabelForPropertyType(typeCastType),
              value: typeCastType,
            };
          });

          return [{items}];
        },

        getListQuantifierSections: () => {
          const listQuantifier = this.state.propertyFilter.listQuantifier;
          const items = [
            {label: `Any`, value: ListQuantifier.Any, isSelected: listQuantifier === ListQuantifier.Any},
            {label: `All`, value: ListQuantifier.All, isSelected: listQuantifier === ListQuantifier.All},
          ];
          return [{items}];
        },

        getDateRangePickerDisplayContext: () => {
          return DATE_RANGE_DISPLAY_CONTEXT_MAP[this.helpers.getDisplayContext()];
        },

        getFilterOperators: () => {
          return this.state.propertyFilter.getFilterOperators();
        },

        focusInput: (vnode) => rafRetry(() => vnode.elm.focus()),

        isLoadingPropertyValues: () => {
          return !this.state.propertyValues;
        },

        isCampaignMessagesPropertyFilter: () => {
          const {propertyName} = this.state.propertyFilter;
          return propertyName === MappedPropertyValues.Campaigns;
        },

        getMessagesSentSections: () => {
          const {campaignVariants = []} = getPropertyValueMappings() || {};
          const {filterValue: selectedCampaignId} = this.state.propertyFilter;
          const items = campaignVariants.map((campaign) => {
            return {
              label: campaign.name,
              value: campaign.id,
              isSelected: selectedCampaignId === campaign.id,
            };
          });

          return [
            {
              items: sortBy(items, [`label`]),
            },
          ];
        },

        renamePropertyValue: (propertyValue) => {
          return renamePropertyValue(propertyValue, this.state.propertyFilter.propertyName);
        },

        sectionsFromInputFn: (searchFilter: string) => {
          if (
            searchFilter &&
            !this.isExperimentsPropertyFilter &&
            searchFilter.length <= MAX_SPECIFY_VALUE_LENGTH &&
            !this.attr(`disable-specify`)
          ) {
            return [
              {
                items: [
                  {
                    value: searchFilter,
                    added: true,
                    label: `Specify: ${searchFilter}`,
                  },
                ],
              },
            ];
          }

          return null;
        },

        getPropertyValueItems: (): SectionItem[] => {
          let {filterValue, propertyName, propertyType, joinPropertyType} = this.state.propertyFilter;
          let propertyObjectKey: string | number | string[] = this.state.propertyFilter.propertyObjectKey;
          let propertyValues = this.state.propertyValues || [];
          const isObject = propertyType === PropertyType.Object;

          if (Array.isArray(propertyObjectKey)) {
            propertyObjectKey = propertyObjectKey[0];
          }

          // object property types define a different selection
          if (
            joinPropertyType === PropertyType.Object &&
            propertyName === MPProperties.Experiments &&
            Array.isArray(this.state.propertyFilter.propertyObjectKey)
          ) {
            return this.getPropertyObjectKeyValues(propertyObjectKey);
          } else if (isObject && !this.isExperimentsPropertyFilter) {
            propertyValues = this.getPropertyObjectKeys();
          } else if (this.isExperimentsPropertyFilter) {
            // $experiments display the name of a given experiment id
            propertyName = MappedPropertyValues.ExperimentId;
            propertyObjectKey = propertyObjectKey.toString();
          }

          if (this.helpers.isCampaignMessagesPropertyFilter()) {
            const {campaignVariants = []} = getPropertyValueMappings() || {};
            const campaignVariantIds = new Set(campaignVariants.map((campaignVariant) => campaignVariant.id));
            propertyValues = propertyValues.filter((propertyValue) =>
              campaignVariantIds.has(parseInt(propertyValue, 10)),
            );
          }

          // There is a case where propertyValue = null. getPropertyObjectKeys() function returns an array [null]
          // when this.state.propertyFilter is null. Therefore, we filter out this case.
          return propertyValues
            .filter((propertyValue) => !isNil(propertyValue))
            .map((propertyValue) => {
              const isSelected =
                (isObject && propertyObjectKey === propertyValue) ||
                (Array.isArray(filterValue) && filterValue.includes(propertyValue));

              const constantPropertyInfo = getOwnAttr<ConstantPropertyValueInfo>(
                ConstantPropertyValuesInfo,
                propertyValue as ConstantPropertyValues,
              );

              return {
                label: renamePropertyValue(propertyValue, propertyName),
                isSelected,
                propertyValue,
                tooltip: constantPropertyInfo?.tooltip,
                labelTheme: constantPropertyInfo ? LabelTheme.Italic : null,
              };
            });
        },

        getPropertyValueSections: (): Section[] => {
          return [
            {
              isLoading: this.state.loadingPropertyValues,
              items: this.helpers.getPropertyValueItems(),
            },
          ];
        },

        /** Fetches items section for values belonging to the selected key */
        getObjectPropertyValueSections: () => {
          const {filterValue, propertyObjectKey} = this.state.propertyFilter;

          if (!propertyObjectKey) {
            return [{items: []}];
          }

          const items = this.getPropertyObjectKeyValues(propertyObjectKey).map((entry) => {
            return {
              ...entry,
              isSelected:
                entry.isSelected ??
                (entry.value === filterValue || (Array.isArray(filterValue) && filterValue.includes(entry.value))),
            };
          });

          return [{items}];
        },

        insertedIconInput: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.focus();
          }),

        // endregion

        // region Template event handlers

        propagateEvent: (ev) => {
          propagateEvent(this, ev);
        },

        dispatchDraw: () => {
          this.dispatchEvent(new CustomEvent(`draw`));
        },

        clickBackButton: () => {
          this.dispatchEvent(new CustomEvent(`clickBack`));
        },

        clickPropertyTypeButton: () => {
          this.update({editingPropertyType: true});
        },

        changePropertyTypesMenu: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({editingPropertyType: false});
          }
        },

        clickPropertyTypesMenuItem: (ev) => {
          this.update({editingPropertyType: false});
          this.changePropertyFilterAttribute(`propertyType`, ev.detail.item.value);
        },

        handleListQuantifierSelect: (ev) => {
          this.changePropertyFilterAttribute(`listQuantifier`, ev.detail.item.value);
        },

        changeFilterOperator: (filterOperator) => {
          this.changePropertyFilterAttribute(`filterOperator`, filterOperator);
        },

        changeFilterValue: (filterValue) => {
          this.changePropertyFilterAttribute(`filterValue`, filterValue);
        },

        toggleFilterItem: (item) => {
          if (this.attr(`disable-multi-select`)) {
            this.helpers.unselectAllPropertyValues();
          }
          this.selectItems([{...item, isSelected: !item.isSelected}]);
        },

        selectAllMatching: ({detail}) => {
          const items = detail.matchingSections.length ? detail.matchingSections[0].items : [];
          const isSelected = !items.every(({isSelected}) => isSelected);

          this.selectItems(items.map((item) => ({...item, isSelected})));
          this.dispatchEvent(new CustomEvent(`multipartFilterSelection`, {detail: {items}}));
        },

        selectAllPropertyValues: ({detail}) => {
          if (detail.selectionState === SelectionState.All) {
            this.helpers.changeFilterValue([]);
          } else {
            this.helpers.changeFilterValue(this.state.propertyValues);
          }
        },

        unselectAllPropertyValues: () => {
          this.helpers.changeFilterValue([]);
        },

        changeSearchFilter: (searchFilter) => {
          this.update({searchFilter});
        },

        selectContainsPropertyValue: (propertyValue) => {
          const propertyFilter = this.state.propertyFilter.setAttribute(`filterValue`, propertyValue);
          this.changePropertyFilter(propertyFilter);
          this.commitPropertyFilter(propertyFilter);
        },

        selectObjectPropertyKey: (ev) => {
          this.changePropertyFilterAttribute(`propertyObjectKey`, ev.detail.item.propertyValue.toString());
        },

        selectObjectPropertyValue: (ev) => {
          const {added, value} = ev.detail.item;
          this.helpers.changeFilterValue(value !== null ? [value] : null);

          // check if item is defined inline
          if (added) {
            this.update({currentValues: [...this.state.currentValues, value]});
          }
        },

        changeDateRange: (ev) => {
          const {dateRange} = ev.detail;
          const propertyFilter = this.state.propertyFilter.setDateRange(dateRange);
          this.changePropertyFilter(propertyFilter);
        },

        clickAddFilter: () => {
          this.commitPropertyFilter(this.state.propertyFilter);
        },

        // endregion
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    this.updateState();

    switch (attr) {
      case `event-name`:
      case `event-names`:
        this.fetchPropertyValues();
        break;
      default:
        break;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateState();

    this.fetchPropertyValues();

    this.handleKeyPress = (ev) => {
      if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.commitPropertyFilter(this.state.propertyFilter);
      }
    };

    this.el.addEventListener(`keydown`, this.handleKeyPress);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.el.removeEventListener(`keydown`, this.handleKeyPress);
  }

  // region Property filter methods

  changePropertyFilter(newPropertyFilter) {
    this.update({propertyFilter: newPropertyFilter});

    this.dispatchEvent(
      new CustomEvent(`changePropertyFilter`, {
        detail: {
          propertyFilter: newPropertyFilter,
        },
      }),
    );
  }

  changePropertyFilterAttribute(key, value) {
    const newPropertyFilter = this.state.propertyFilter.setAttribute(key, value);

    // attempt to preserve current filter value for the selected filterOperator
    if (key === `filterOperator`) {
      const {filterValue: currentFilterValue} = this.state.propertyFilter;
      const defaultFilterValue = newPropertyFilter.filterValue;
      newPropertyFilter.filterValue = currentFilterValue;

      if (newPropertyFilter.isValid() === false) {
        newPropertyFilter.filterValue = defaultFilterValue;
      }
    }

    this.changePropertyFilter(newPropertyFilter);
  }

  commitPropertyFilter(propertyFilter) {
    if (propertyFilter.isValid()) {
      this.dispatchEvent(
        new CustomEvent(`commitPropertyFilter`, {
          detail: {
            propertyFilter,
          },
        }),
      );
    }
  }

  selectItems(items) {
    const filterValue = new Set(this.state.propertyFilter.filterValue as string[]);

    items.forEach((item) => {
      // when a user specifies a value that is not among the provided property values, it is captured in item.value
      const value = Object.prototype.hasOwnProperty.call(item, `propertyValue`) ? item.propertyValue : item.value;
      if (item.isSelected) {
        filterValue.add(value);
      } else {
        filterValue.delete(value);
      }

      if (!Object.prototype.hasOwnProperty.call(item, `propertyValue`)) {
        this.update({
          currentValues: [...(this.state.currentValues || []), item.value],
          propertyValues: [...(this.state.propertyValues || []), item.value],
        });
      }
    });

    this.helpers.changeFilterValue([...filterValue]);
  }

  // endregion

  get projectId() {
    return this.attr(`project-id`);
  }

  get workspaceId() {
    return this.attr(`workspace-id`);
  }

  get isExperimentsPropertyFilter() {
    const {propertyName, propertyType, joinPropertyType} = this.state.propertyFilter;

    return (
      (propertyType === PropertyType.Object || joinPropertyType === PropertyType.Object) &&
      propertyName === MPProperties.Experiments
    );
  }

  get isDatasetPropertyFilter() {
    const {propertyName} = this.state.propertyFilter;
    return propertyName === DATASET_PROPERTY_NAME;
  }

  // region Set up / data fetching

  updateState() {
    const propertyFilterAttrs = this.attr(`property-filter`);
    const displayContext = this.helpers.getDisplayContext();
    const propertyFilter = PropertyFilterFactory.createPropertyFilter(propertyFilterAttrs, {displayContext});

    this.update({propertyFilter});
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async fetchPropertyValues() {
    if (!this.state.propertyFilter) {
      return; // not ready to query yet
    }

    this.update({loadingPropertyValues: true});

    if (this.projectId) {
      await syncReportPropertyValueMappings(this.projectId, this.workspaceId);
    }

    let propertyValues = [];
    const {filterValue, propertyType, propertyName, resourceType, filterDateUnit} = this.state.propertyFilter;
    const isString = propertyType === PropertyType.String;
    const isObject = propertyType === PropertyType.Object;
    const currentValues = Array.isArray(filterValue) && (isString || isObject) ? filterValue : [];

    // ensure that selected values are passed into checkbox component so that they may be unselected
    const appendCurrentValues = (values) =>
      uniq([...values, ...(propertyType === PropertyType.String ? currentValues : [])]);

    try {
      // $experiments requires a special mapping/display of options
      if (this.isExperimentsPropertyFilter) {
        propertyValues = this.getExperiments().map((experiment) => experiment.id.toString());
      } else if (this.isDatasetPropertyFilter) {
        propertyValues = Object.keys(this.getDatasets());
      } else if (filterDateUnit === SpecialTimeUnit.DayOfWeek) {
        propertyValues = DAYS_OF_THE_WEEK;
      } else if (filterDateUnit === SpecialTimeUnit.HourOfDay) {
        propertyValues = HOURS_OF_DAY;
      } else if (resourceType === ResourceType.Event) {
        propertyValues = await this.fetchEventPropertyValues();
        propertyValues = appendCurrentValues(propertyValues);
      } else if (resourceType === ResourceType.User) {
        propertyValues = await this.fetchPeoplePropertyValues();
        propertyValues = appendCurrentValues(propertyValues);
      } else if (resourceType === ResourceType.Other && propertyName === FLOWS_CONVERSION.value) {
        // TODO: consider adding a new property values endpoint for ResourceType.Other if more such properties are added
        propertyValues = FLOWS_CONVERSION_VALUES;
      }
    } catch (err) {
      console.error(err);
    }

    const filterSelection = Array.isArray(filterValue) ? filterValue : [];
    this.update({
      currentValues,
      loadingPropertyValues: false,
      propertyValues: sortBy(
        propertyValues,
        // makes sure selected & special constant values surface to the top
        [(item) => !filterSelection.includes(item), (item) => ConstantPropertyValueSet.has(item) === false],
      ),
    });
  }

  async fetchEventPropertyValues() {
    const eventNames = this.attr(`event-names`) && uniq(this.attr(`event-names`));
    const listPropertyName = this.params.listProperty?.propertyName;
    const {
      propertyName,
      joinPropertyType,
      listQuantifier,
      propertyObjectKey,
      customProperty,
      customPropertyId,
      datasetId,
      dataGroupId,
      tempDataGroupId,
    } = this.state.propertyFilter;

    // if events like `$all_events` are passed, all events should be queried
    const includeAllProperties =
      !eventNames ||
      eventNames.some((eventName: string) => {
        return [null, SPECIAL_EVENTS.ALL_EVENTS, SPECIAL_EVENTS.TOP_EVENTS, SPECIAL_EVENTS.ANY_EVENT].includes(
          eventName,
        );
      });
    const isObjectProperty = joinPropertyType === PropertyType.Object && propertyObjectKey?.length;
    const includePropertyName = !customProperty && !customPropertyId && !isObjectProperty && !listPropertyName;

    return eventPropertyValuesClient.query({
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ...(datasetId ? {datasetId} : {}),
      ...(includeAllProperties ? {} : {eventName: eventNames}),
      projectId: this.projectId,
      ...(joinPropertyType === PropertyType.Object && propertyObjectKey?.length
        ? {
            objectProperty: this.state.propertyFilter.getAttributes(),
          }
        : {}),
      ...(customProperty
        ? {customProperty: {customProperty}}
        : customPropertyId
        ? {customProperty: {customPropertyId}}
        : {}),
      ignoreListItem: !listQuantifier,
      ...(listPropertyName
        ? {
            objectProperty: {
              resourceType: ResourceType.Event,
              propertyType: `list`,
              propertyName: listPropertyName,
              propertyObjectKey: [propertyName],
            },
          }
        : {}),
      ...(includePropertyName ? {propertyName} : {}),
      ...(Array.isArray(propertyName) ? {dataGroupPropertyName: propertyName[1]} : {}),
      workspaceId: this.workspaceId,
    });
  }

  fetchPeoplePropertyValues() {
    const {
      propertyName,
      customProperty,
      joinPropertyType,
      propertyObjectKey,
      listQuantifier,
      datasetId,
      dataGroupId,
      tempDataGroupId,
    } = this.state.propertyFilter;
    const isObjectProperty = joinPropertyType === PropertyType.Object && propertyObjectKey?.length;
    const includePropertyName = !customProperty && !isObjectProperty;
    const listProperty = this.params.listProperty;

    return peoplePropertyValuesClient.query({
      projectId: this.projectId,
      ...(customProperty ? {customProperty: {customProperty}} : {}),
      ...(includePropertyName ? {propertyName} : {}),
      ...(Array.isArray(propertyName) ? {dataGroupPropertyName: propertyName[1]} : {}),
      ...(datasetId ? {datasetId} : {}),
      ...(joinPropertyType === PropertyType.Object && propertyObjectKey?.length
        ? {
            objectProperty: this.state.propertyFilter.getAttributes(),
          }
        : {}),
      ...(listProperty
        ? {
            objectProperty: {
              resourceType: ResourceType.User,
              propertyType: `list`,
              propertyName: listProperty.propertyName,
              propertyObjectKey: [propertyName],
            },
          }
        : {}),
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      ignoreListItem: !listQuantifier,
      workspaceId: this.workspaceId,
    });
  }

  getExperiments() {
    const mappings = getPropertyValueMappings() || {};

    return mappings[`experiments`] || [];
  }

  getDatasets() {
    const mappings = getPropertyValueMappings() || {};

    return mappings[`datasets`] || [];
  }

  /**
   * For object property types, parse their JSON values
   * TODO(pablo): The API returns malformed blobs in some instances, to
   * avoid failing the whole selection menu, catch those errors and
   * display partial results
   */
  getObjectPropertyValues() {
    const propertyValues = this.state.propertyValues || [];

    return propertyValues
      .map((value) => {
        try {
          return JSON.parse(value);
        } catch (err) {
          console.warn(`Error parsing object property values`, err, value);
          return {};
        }
      })
      .filter((x) => x);
  }

  getPropertyObjectKeys() {
    const {propertyObjectKey} = this.state.propertyFilter;
    const propertyValues = this.getObjectPropertyValues();

    const keys = new Set(propertyObjectKey ? [propertyObjectKey as string] : []);
    propertyValues.forEach((value) => Object.keys(value).forEach((key) => keys.add(key)));

    return Array.from(keys);
  }

  /**
   * Returns a list of section items that belong
   * to a given object entry, or variants that
   * belong to a given experiment
   */
  getPropertyObjectKeyValues(selectedKey) {
    const {currentValues} = this.state;
    const {filterValue} = this.state.propertyFilter;
    const propertyValues = this.getObjectPropertyValues();

    // $experiments allows for a selection of the variants within a given experiemnt
    if (this.isExperimentsPropertyFilter) {
      selectedKey = parseFloat(selectedKey);
      const experiments = this.getExperiments();
      const selectedExperiment = experiments.find((experiment) => experiment.id === selectedKey);
      const variants = selectedExperiment.variants.map((variant, index) => {
        const value = variant.id.toString();
        const label = index === 0 ? `Original variant` : `Variant ${index}`;

        const isSelected =
          value === filterValue ||
          (Array.isArray(filterValue) &&
            (filterValue.includes(variant.id) || filterValue.includes(variant.id.toString())));
        return {label, value, isSelected};
      });

      if (
        Array.isArray(this.state.propertyFilter.propertyObjectKey) &&
        this.state.propertyFilter.joinPropertyType === PropertyType.Object
      ) {
        return variants;
      }

      return [{label: `Any variant`, value: null}, ...variants];
    }

    // displays only plain values to avoid persisting
    // string representations of object-like values
    const values = [
      // append existing unkown values so they render in the menu
      ...currentValues,
      // map all values for the selected key from the loaded prop values
      ...propertyValues.map((value) => value[selectedKey]),
    ].filter((value) => isNumber(value) || isString(value) || isBoolean(value));

    return [
      {label: `Any value`, value: null},
      ...Array.from(new Set(values)).map((value) => ({label: value.toString(), value})),
    ];
  }

  shouldUpdate(stateUpdate) {
    const state = {...this.state, ...stateUpdate};
    return state.propertyFilter && state.propertyFilter.propertyName && state.propertyFilter.propertyType;
  }

  enhancedListFilterEnabled() {
    return isEnhancedListFilter(this.state.propertyFilter) && this.attr(`enable-enhanced-list-filters`);
  }

  // endregion
}

export const PropertyValuesScreenEl = defineAndMakeJsxFactory(`mp-property-values-screen`, PropertyValuesScreen);
export const PropertyValuesScreenReact = panel2react(`mp-property-values-screen`, PropertyValuesScreen);
